import { createContext, useContext } from "react";
import type { ModelApi } from "../hooks/useModel.js";

/** The shared compile plus provider-level controls. `flush` collapses the
 *  recompile debounce NOW — the mutation layer calls it after applying an
 *  atomic edit (a gesture is one write, not a keystroke stream; WYSIWYG plan
 *  §8), so the board reflects the mutation without the 150 ms wait. */
export interface WorkspaceModelApi extends ModelApi {
  flush: () => void;
}

export const WorkspaceModelContext = createContext<WorkspaceModelApi | null>(null);

/**
 * Emission-level diagnostic suppression: strip everything below an error from
 * the shared compile so no downstream surface (board badges, minimap, nav,
 * details) shows TODO/warning noise. Identity when `quiet` is off. Driven by
 * {@link QuietDiagnosticsContext} — the scratch surface turns it on.
 */
export function filterToErrors<T extends ModelApi>(model: T, quiet: boolean): T {
  if (!quiet) return model;
  return {
    ...model,
    diagnostics: model.diagnostics.filter((d) => d.severity === "error"),
  };
}

/** The shared compiled model. Throws outside a `WorkspaceModelProvider`. */
export function useWorkspaceModel(): WorkspaceModelApi {
  const m = useContext(WorkspaceModelContext);
  if (!m) {
    throw new Error("useWorkspaceModel must be used within a <WorkspaceModelProvider>");
  }
  return m;
}
